import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Smartphone, ShieldCheck, Warehouse } from 'lucide-react';

import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import GoogleSignInButton from '@/components/auth/GoogleSignInButton';

const initialOfficeForm = { email: '', password: '' };
const initialOtpForm = { phone: '', otp: '' };

const persistLogin = (data) => {
  localStorage.setItem('token', data.access_token);
  localStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
  localStorage.setItem('menu_items', JSON.stringify(data.menu_items || []));
};

const Login = () => {
  const [mode, setMode] = useState('office');
  // Driver / Godown OTP flows still exist (mobile-first personas) but the
  // office login is the only thing 99% of staff need. Show the OTP tabs
  // only when the user explicitly clicks "Driver or Godown manager?".
  const [showOtpModes, setShowOtpModes] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [officeForm, setOfficeForm] = useState(initialOfficeForm);
  const [driverForm, setDriverForm] = useState(initialOtpForm);
  const [godownForm, setGodownForm] = useState(initialOtpForm);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendAfter, setResendAfter] = useState(0);
  const navigate = useNavigate();

  // Reset OTP state when switching modes
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setOtpSent(false);
    setResendAfter(0);
  };

  const otpButtonLabel = useMemo(() => {
    if (loading && !otpSent) return 'Sending OTP...';
    if (loading && otpSent) return 'Verifying...';
    return otpSent ? 'Verify OTP' : 'Send OTP';
  }, [loading, otpSent]);

  React.useEffect(() => {
    if (resendAfter <= 0) return undefined;
    const timer = window.setTimeout(() => setResendAfter((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendAfter]);

  const handleOfficeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authAPI.login(officeForm);
      persistLogin(data);
      toast.success('Login successful');
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Google SSO ----
  // `credential` is the Google ID token handed to the browser by Google
  // Identity Services. The backend verifies it and returns the same session
  // payload as the password login, so persistLogin() is shared.
  const handleGoogleCredential = async (credential) => {
    setLoading(true);
    try {
      const { data } = await authAPI.googleLogin({ credential });
      persistLogin(data);
      toast.success('Signed in with Google');
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // ---- Driver OTP ----
  const handleSendDriverOtp = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.sendDriverOtp({ phone: driverForm.phone });
      setOtpSent(true);
      setResendAfter(data.resend_after_seconds || 60);
      toast.success(data.message || 'OTP sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDriverOtp = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.verifyDriverOtp(driverForm);
      persistLogin(data);
      toast.success('Driver login successful');
      navigate('/driver-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    if (otpSent) { await handleVerifyDriverOtp(); return; }
    await handleSendDriverOtp();
  };

  // ---- Godown Manager OTP ----
  const handleSendGodownOtp = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.sendGodownManagerOtp({ phone: godownForm.phone });
      setOtpSent(true);
      setResendAfter(data.resend_after_seconds || 60);
      toast.success(data.message || 'OTP sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyGodownOtp = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.verifyGodownManagerOtp(godownForm);
      persistLogin(data);
      toast.success('Godown manager login successful');
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGodownSubmit = async (e) => {
    e.preventDefault();
    if (otpSent) { await handleVerifyGodownOtp(); return; }
    await handleSendGodownOtp();
  };

  // Generic resend for the active OTP mode
  const handleResendOtp = () => {
    if (mode === 'driver') handleSendDriverOtp();
    else if (mode === 'godown') handleSendGodownOtp();
  };

  const activeOtpForm = mode === 'driver' ? driverForm : godownForm;
  const setActiveOtpForm = mode === 'driver' ? setDriverForm : setGodownForm;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md border-border/60 shadow-sm" data-testid="login-form">
        <CardHeader className="space-y-5">
          <div className="text-center">
            <CardTitle className="text-3xl font-display tracking-tight text-foreground">
              report360
            </CardTitle>
            <CardDescription className="text-sm mt-2 text-muted-foreground">
              Sign in to your operations workspace
            </CardDescription>
          </div>

          {/* OTP tabs only render after the user clicks the small footer
              link "Driver or Godown manager? Sign in via OTP →". Keeps the
              default login screen clean for office staff (the 99% case). */}
          {showOtpModes && (
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
              {[
                { key: 'office', label: 'Office' },
                { key: 'driver', label: 'Driver OTP' },
                { key: 'godown', label: 'Godown OTP' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleModeChange(key)}
                  className={`rounded-md px-2 py-2 text-xs font-medium transition-colors ${
                    mode === key
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {mode === 'office' ? (
            <>
            <form onSubmit={handleOfficeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@fleet.com"
                  required
                  data-testid="email-input"
                  value={officeForm.email}
                  onChange={(e) => setOfficeForm({ ...officeForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                  value={officeForm.password}
                  onChange={(e) => setOfficeForm({ ...officeForm, password: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Login
                  </>
                )}
              </Button>
            </form>
            <GoogleSignInButton onCredential={handleGoogleCredential} disabled={loading} />
            </>
          ) : (
            /* Shared OTP form used by both Driver and Godown Manager modes */
            <form
              onSubmit={mode === 'driver' ? handleDriverSubmit : handleGodownSubmit}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="otp-phone">
                  {mode === 'driver' ? 'Driver' : 'Godown Manager'} Mobile Number
                </Label>
                <Input
                  id="otp-phone"
                  type="tel"
                  placeholder="9876543210"
                  required
                  value={activeOtpForm.phone}
                  onChange={(e) =>
                    setActiveOtpForm({
                      ...activeOtpForm,
                      phone: e.target.value,
                      otp: otpSent ? activeOtpForm.otp : '',
                    })
                  }
                  disabled={loading || otpSent}
                />
              </div>

              {otpSent && (
                <div className="space-y-3">
                  <Label htmlFor="otp-input">Enter OTP</Label>
                  <InputOTP
                    id="otp-input"
                    maxLength={6}
                    value={activeOtpForm.otp}
                    onChange={(value) => setActiveOtpForm({ ...activeOtpForm, otp: value })}
                    disabled={loading}
                  >
                    <InputOTPGroup className="w-full justify-between">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <InputOTPSlot key={index} index={index} className="h-12 w-12 rounded-md border" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || (otpSent && activeOtpForm.otp.length !== 6)}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {otpButtonLabel}
                  </>
                ) : (
                  <>
                    {mode === 'godown' ? (
                      <Warehouse className="mr-2 h-4 w-4" />
                    ) : (
                      <Smartphone className="mr-2 h-4 w-4" />
                    )}
                    {otpButtonLabel}
                  </>
                )}
              </Button>

              {otpSent && (
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading || resendAfter > 0}
                    className="font-medium text-sky-700 disabled:text-slate-400"
                  >
                    Resend OTP
                  </button>
                  <span className="text-slate-500">
                    {resendAfter > 0 ? `Retry in ${resendAfter}s` : 'OTP active'}
                  </span>
                </div>
              )}
            </form>
          )}

          {/* Footer link — reveals Driver / Godown OTP tabs only when
              tapped. Hidden by default to keep the office login uncluttered. */}
          {!showOtpModes && (
            <div className="mt-5 pt-4 border-t border-border/50 text-center">
              <button
                type="button"
                onClick={() => setShowOtpModes(true)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Driver or Godown manager? <span className="text-indigo-600 hover:underline">Sign in via OTP →</span>
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Forgot password modal — interim guidance until email-based reset
          is wired. Tells the user to contact their admin. The recipient
          list is the seeded admin user (alok.k@) + IT support fallback. */}
      {forgotOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setForgotOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-slate-900">Reset your password</h2>
            <p className="mt-2 text-sm text-slate-600">
              Email-based self-service reset isn't set up yet. To regain access:
            </p>
            <ol className="mt-3 ml-4 list-decimal text-sm text-slate-700 space-y-1.5">
              <li>Drop a message to your IT / admin:</li>
              <li className="list-none ml-0">
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 my-2 text-xs">
                  <div><span className="text-slate-500">IT:</span> <a className="text-indigo-700 hover:underline" href="mailto:pratul.udainiya@rechargestudio.com">pratul.udainiya@rechargestudio.com</a></div>
                  <div><span className="text-slate-500">Admin:</span> <a className="text-indigo-700 hover:underline" href="mailto:admin@fleet.com">admin@fleet.com</a></div>
                </div>
              </li>
              <li>Share your registered email — they will issue a one-time temporary password.</li>
              <li>Sign in with the temp password, then change it from your <span className="font-medium">Profile → Change Password</span>.</li>
            </ol>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setForgotOpen(false)} variant="outline" className="text-sm">
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
